import {
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding, HostListener,
  Input, OnDestroy, OnInit,
  Output,
} from '@angular/core';
import { PopoverComponent } from './popover.component';
import {
  computePosition,
  flip,
  Middleware,
  Placement,
  size,
} from '@floating-ui/dom';
import { Subscription } from 'rxjs';
import { transformOrigin } from '../../floating-middlewares/transform-origin';
import { Keys } from '../../../_core/utils/keys';

@Directive({
  selector: '[opacPopoverTrigger]',
  exportAs: 'opacPopoverTrigger',
})
export class PopoverTriggerDirective implements OnDestroy, OnInit {
  private closeSubscription?: Subscription;
  protected isOpen = false;

  /**
   * Sets aria-expanded attribute
   */
  @HostBinding('attr.aria-expanded')
  get ariaExpanded() {
    return this.isOpen;
  }

  /**
   * Sets aria-details attribute
   */
  @HostBinding('attr.aria-details')
  get ariaDetails() {
    return this.popoverComponent.id;
  }

  /**
   * Listens for host click actions and opens popover if needed
   */
  @HostListener('click', ['$event'])
  onHostClick(event: Event) {
    if (this.cancelHostInteraction) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (!this.isOpen) {
      this.showPopover();
    }
  }

  /**
   * Listens for host keydown event and opens the popup if Enter or Space are pressed
   */
  @HostListener('keydown', ['$event'])
  onHostKeydown(event: KeyboardEvent) {
    if (this.cancelHostInteraction) {
      return;
    }

    if ([Keys.Enter, Keys.Space].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      this.onHostClick(event);
    }
  }

  /**
   * Listens for host keyup event to prevent a Firefox bug
   */
  @HostListener('keyup', ['$event'])
  onHostKeyup(event: KeyboardEvent) {
    // Prevent space from triggering a click event in Firefox
    if (event.key === ' ') {
      event.preventDefault();
    }
  }

  /**
   * Popover component reference
   */
  @Input('opacPopoverTrigger') popoverComponent!: PopoverComponent;
  /**
   * Prevent using host listeners if true
   */
  @Input('opacPopoverTriggerCancelHostInteraction') cancelHostInteraction = false;
  /**
   * Max popover height. It won't be greater than `size()` middleware calc.
   */
  @Input('opacPopoverTriggerMaxHeight') maxHeight = Infinity;
  /**
   * Min popover width. It won't be greater than `size()` middleware calc.
   */
  @Input('opacPopoverTriggerMinWidth') minWidth = 0;
  /**
   * Max popover width. It won't be greater than `size()` middleware calc.
   */
  @Input('opacPopoverTriggerMaxWidth') maxWidth = Infinity;
  /**
   * Array of middlewares to insert before when computing position
   */
  @Input('opacPopoverTriggerBeforeMiddlewares') beforeMiddlewares: Middleware[] = [];
  /**
   * Array of middlewares to insert after when computing position
   */
  @Input('opacPopoverTriggerAfterMiddlewares') afterMiddlewares: Middleware[] = [];
  /**
   * Sets default position of popover
   */
  @Input('opacPopoverTriggerPosition') position: Placement = 'bottom-start';
  /**
   * Emits when popover closes
   */
  @Output() popoverClose = new EventEmitter<void>();
  /**
   * Emits when popover opens
   */
  @Output() popoverOpen = new EventEmitter<void>();

  constructor(
    protected readonly elementRef: ElementRef<HTMLElement>,
  ) {
  }

  ngOnInit() {
    // This is called when popover `toggle` event is raised with ToggleEvent.newState === 'closed' (light dismissed)
    this.closeSubscription = this.popoverComponent.popoverClosed$.subscribe(() => {
      this.closePopover();
    });
  }

  ngOnDestroy() {
    this.closeSubscription?.unsubscribe();
  }

  closePopover() {
    // Set popover closed
    this.popoverComponent.isOpen = false;
    // Set directive as closed
    this.isOpen = false;
    // Emit close event
    this.popoverClose.emit();
    // Close popover
    (this.popoverComponent.popover.nativeElement as any).hidePopover();
  }

  async showPopover() {
    const referenceEl = this.elementRef.nativeElement;
    const popoverEl = this.popoverComponent.popover.nativeElement;
    const maxHeight = this.maxHeight;
    const maxWidth = this.maxWidth;
    const minWidth = this.minWidth;

    // Show popover before compute its position
    (this.popoverComponent.popover.nativeElement as any).showPopover();
    // Set popover open
    this.popoverComponent.isOpen = true;
    // Set directive as open
    this.isOpen = true;
    // Emit open event
    this.popoverOpen.emit();

    // Wait for the content to be renderer to compute the popover position
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    const { x, y } = await computePosition(referenceEl, popoverEl, {
      placement: this.position,
      middleware: [
        // Apply custom middlewares
        ...this.beforeMiddlewares,
        // Flip popover in case there is no space
        flip(),
        // Adjust max width/height and overflow popover
        size({
          padding: 5,
          apply({ availableWidth, availableHeight, elements }) {
            Object.assign(elements.floating.style, {
              minWidth: `${minWidth}px`,
              maxWidth: `${maxWidth}px`,
              maxHeight: `${Math.min(maxHeight, availableHeight)}px`,
            });
          },
        }),
        // Apply transform-origin based in its final placement
        transformOrigin(),
        // Apply custom middlewares
        ...this.afterMiddlewares,
      ],
    });

    this.popoverComponent.top = y;
    this.popoverComponent.left = x;
  }
}
